import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from 'react'
import { Platform, Pressable, Text, View } from 'react-native'
import { usePathname } from 'expo-router'
import Animated, { FadeIn, FadeOut, useAnimatedKeyboard, useAnimatedStyle } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import type { SvgProps } from 'react-native-svg'

import { cn } from '@/utils/cn'
import { CheckCircle, Close, Error, Info, Warning } from '@/components/icon'

type ToastVariant = 'default' | 'success' | 'error' | 'info' | 'warning'

type ToastOptions = {
  variant?: ToastVariant
  title?: string
  description: string
  closeButton?: boolean
}

interface ToastState {
  show: (options: ToastOptions) => void
  hide: () => void
}

const ToastContext = createContext<ToastState>({
  show: () => {},
  hide: () => {}
})

const DURATION = 3000

interface ToastProviderProps {
  children: ReactNode
  duration?: number
}

export function ToastProvider({ children, duration = DURATION }: ToastProviderProps) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [options, setOptions] = useState<ToastOptions | null>(null)
  const insets = useSafeAreaInsets()
  const pathname = usePathname()

  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const show = useCallback((options: ToastOptions) => {
    if (pathname !== '/landing') {
      reset()
      setOptions(options)
    }
  }, [])

  const hide = useCallback(() => {
    reset()
    setOptions(null)
  }, [])

  useEffect(() => {
    if (options) {
      timeoutRef.current = setTimeout(() => {
        setOptions(null)
      }, duration)
    }
    return reset
  }, [options, reset])

  const value = useMemo(() => ({ show, hide }), [show, hide])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <View
        className={'absolute size-full items-center justify-center px-4'}
        style={{ paddingTop: insets.top, paddingBottom: insets.bottom }}
        pointerEvents={'box-none'}
      >
        {options && <Toast options={options} hide={hide} />}
      </View>
    </ToastContext.Provider>
  )
}

export function useToast() {
  return useContext(ToastContext)
}

const ICON_MAP: Record<ToastVariant, FC<SvgProps> | undefined> = {
  success: CheckCircle,
  error: Error,
  info: Info,
  warning: Warning,
  default: undefined
}

interface ToastProps {
  options: ToastOptions
  hide: () => void
}

function Toast({ options, hide }: ToastProps) {
  // `useAnimatedKeyboard` isn't available on web, guard the hook
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const keyboard = Platform.OS === 'web' ? undefined : useAnimatedKeyboard()
  const style = useAnimatedStyle(() => ({
    transform: [{ translateY: keyboard ? -keyboard.height.value / 2 : 0 }]
  }))

  const { variant = 'default', title, description, closeButton = false } = options
  const Icon = ICON_MAP[variant]

  return (
    <Animated.View style={[style]} entering={FadeIn} exiting={FadeOut}>
      <View className={'flex-row rounded-lg border border-transparent bg-black/80 p-1.5'}>
        <View className={'flex-row gap-x-2 p-1.5'}>
          {Icon && <Icon className={'size-5 text-white'} />}
          <View className={'gap-y-1'}>
            {title && <Text className={'text-sm font-semibold text-text-onColor'}>{title}</Text>}
            <Text className={'text-sm text-text-onColor'}>{description}</Text>
          </View>
        </View>
        {closeButton && (
          <Pressable onPress={hide}>
            {({ pressed }) => (
              <View className={cn('bg-fill-flat p-1.5', pressed && 'bg-fill-flat-active')}>
                <Close className={'size-5 text-white'} />
              </View>
            )}
          </Pressable>
        )}
      </View>
    </Animated.View>
  )
}
